import { defineStore } from 'pinia';

function generateHash(obj: any): number {
  const str = JSON.stringify(obj);
  let hash = 0,
    i: number,
    chr: number;
  if (str.length === 0) return hash;
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
}

/** State of the componentData module (generic per-component data cache) */
export interface ComponentDataState {
  /** Cached component data keyed by a hash of the component's props */
  componentData: Record<number, any>;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/componentData.ts` (namespace
 * `componentData`). In-memory only — no `vuexPersist` option (matches the legacy module, which had
 * no reducer entry in `src/store/index.ts`).
 *
 * No name collisions in this module: the single getter (`getComponentData`) and the single
 * mutation-turned-action (`setComponentData`) have different names, so both keep their legacy
 * names unchanged. The legacy `actions` registry was empty.
 */
export const useComponentDataStore = defineStore('componentData', {
  state: (): ComponentDataState => ({
    componentData: {},
  }),
  getters: {
    /**
     * Returns a getter function that resolves cached data for a given props object, hashed to a
     * lookup key. Falls back to an empty object when nothing is cached yet.
     */
    getComponentData(): (props: any) => any {
      return (props: any) => {
        const hash = generateHash(props);
        return this.componentData[hash] || {};
      };
    },
  },
  actions: {
    /**
     * Converted from legacy mutation `setComponentData` (no matching legacy action existed).
     * Caches `payload.data` under a hash of `payload.props`.
     */
    setComponentData(payload: { props: any; data: any }) {
      const hash = generateHash(payload.props);
      this.componentData[hash] = payload.data;
    },
  },
});
